import React, { useEffect, useRef, useMemo, useState } from "react";
import { TaskList } from "../task-list/TaskList";
import { TaskListHeader } from "../task-list-header/TaskListHeader";
import { SectionModal } from "../section-modal/SectionModal";
import "./Dashboard.css";
import { generateId } from "../../utils/Utils";
import { assignElementToGroup, removeNodeById, updateNodeById, replaceNodeById } from "../../utils/treeUtils";
import { saveToStorage, loadFromStorage, STORAGE_KEYS } from "../../utils/storageUtils";
import { DashboardProps, Element, Task, Collection, TrashItem, CollectionGroup } from "../../types";
import { withScheduleStartDate } from '../../utils/taskSchedule';

const GENERAL_GROUP: CollectionGroup = { id: 'general', name: 'General' };

export const Dashboard: React.FC<DashboardProps> = ({
    selectedCollectionId,
    onDeleteCollection,
    folders,
    setFolders,
    hiddenCollectionIds = new Set<string>(),
    onChangeCollectionSecretStatus,
}) => {
    // Find the selected collection in folder collections
    const selectedCollection = useMemo((): Collection => {
        for (const folder of folders || []) {
            const collectionInFolder = (folder.collections || []).find((collection) => collection.id === selectedCollectionId);
            if (collectionInFolder) return collectionInFolder;
        }
        return {} as Collection;
    }, [folders, selectedCollectionId]);
    const isInboxCollection = selectedCollection.name?.trim().toLowerCase() === 'inbox';

    const [showReorderHandles, setShowReorderHandles] = useState(false);
    const [showAddSectionModal, setShowAddSectionModal] = useState(false);

    useEffect(() => setShowReorderHandles(false), [selectedCollectionId]);

    const updateCurrentCollection = (updates: Partial<Collection>) => {
        setFolders(
            folders.map((folder) => ({
                ...folder,
                collections: (folder.collections || []).map((collection) =>
                    collection.id === selectedCollectionId
                        ? { ...collection, ...updates }
                        : collection,
                ),
            })),
        );
    };

    // Combine all collections from folder collections for selection dialogs
    const allCollections = useMemo((): Collection[] => {
        const combined: Collection[] = [];
        for (const folder of folders || []) {
            combined.push(...(folder.collections || []).filter(
                (collection) => !hiddenCollectionIds.has(collection.id),
            ));
        }
        return combined;
    }, [folders, hiddenCollectionIds]);

    const stepsContainerRef = useRef<HTMLDivElement>(null);

    // Save selectedCollectionId to localStorage whenever it changes
    useEffect(() => {
        saveToStorage(STORAGE_KEYS.SELECTED_COLLECTION_ID, selectedCollectionId);
    }, [selectedCollectionId]);

    const updateElementsForSelectedCollection = (updatedElements: Element[]) => {
        updateCurrentCollection({ elements: updatedElements });
    };

    const updateCollectionName = (newName: string) => {
        if (newName.trim().toLowerCase() === 'inbox') {
            alert('Inbox is reserved.');
            return;
        }

        updateCurrentCollection({ name: newName });
    };

    const groups = selectedCollection.grouping?.groups?.length
        ? selectedCollection.grouping.groups
        : [GENERAL_GROUP];

    const handleAddElement = (element: Element, groupId = GENERAL_GROUP.id) => {
        const updatedElements = [...(selectedCollection.elements || []), { ...element, groupId }];
        updateCurrentCollection({
            elements: updatedElements,
            grouping: { mode: selectedCollection.grouping?.mode || 'section', groups },
        });
    };

    const handleAddGroup = (name: string) => {
        const trimmedName = name.trim();
        if (!trimmedName) return;
        updateCurrentCollection({
            grouping: {
                mode: 'section',
                groups: [...groups, { id: generateId(), name: trimmedName }],
            },
        });
    };

    const handleToggleGroup = (groupId: string) => {
        updateCurrentCollection({
            grouping: {
                mode: selectedCollection.grouping?.mode || 'section',
                groups: groups.map((group) => group.id === groupId
                    ? { ...group, collapsed: !group.collapsed }
                    : group),
            },
        });
    };

    const handleRenameGroup = (groupId: string, name: string) => {
        updateCurrentCollection({
            grouping: {
                mode: selectedCollection.grouping?.mode || 'section',
                groups: groups.map((group) => group.id === groupId ? { ...group, name } : group),
            },
        });
    };

    const handleDeleteGroup = (groupId: string) => {
        const remainingGroups = groups.filter((group) => group.id !== groupId);
        if (remainingGroups.length === 0) return;
        const destinationGroupId = groupId === GENERAL_GROUP.id
            ? remainingGroups[0].id
            : (remainingGroups.some((group) => group.id === GENERAL_GROUP.id)
                ? GENERAL_GROUP.id
                : remainingGroups[0].id);
        updateCurrentCollection({
            elements: (selectedCollection.elements || []).map((element) => {
                const currentGroupId = element.groupId || GENERAL_GROUP.id;
                return assignElementToGroup(
                    element,
                    currentGroupId === groupId ? destinationGroupId : currentGroupId,
                );
            }),
            grouping: {
                mode: selectedCollection.grouping?.mode || 'section',
                groups: remainingGroups,
            },
        });
    };

    const findFolderForCollection = (collectionId: string) => {
        for (const folder of folders || []) {
            const collection = (folder.collections || []).find((c) => c.id === collectionId);
            if (collection) {
                return { folderName: folder.name, folderId: folder.id };
            }
        }
        return { folderName: 'Unknown Folder', folderId: undefined };
    };

    const addElementToTrash = (element: Element) => {
        const trash = loadFromStorage<TrashItem[]>(STORAGE_KEYS.TRASH, []);
        if (!trash) return;

        const { folderName, folderId } = findFolderForCollection(selectedCollectionId || '');
        const trashItem: TrashItem = {
            id: generateId(),
            type: 'element',
            element,
            deletedAt: new Date().toISOString(),
            sourceCollectionId: selectedCollectionId,
            sourceFolderId: folderId,
            sourceFolderName: folderName,
            sourceCollectionName: selectedCollection.name,
            secret: Boolean(selectedCollection.secret),
        };
        trash.push(trashItem);
        saveToStorage(STORAGE_KEYS.TRASH, trash);
    };

    const findElementById = (elements: Element[], id: string): Element | null => {
        for (const element of elements) {
            if (element.id === id) return element;
            if (element.type === 'task' && (element as Task).subtasks) {
                const found = findElementById((element as Task).subtasks!, id);
                if (found) return found;
            }
        }
        return null;
    };

    const handleDeleteElement = (id: string) => {
        const elementToDelete = findElementById(selectedCollection.elements || [], id);
        if (elementToDelete) {
            addElementToTrash(elementToDelete);
        }

        const updatedElements = removeNodeById(selectedCollection.elements || [], id);
        updateElementsForSelectedCollection(updatedElements);
    };

    const handleEditElement = (id: string) => {
        const updatedElements = updateNodeById(
            selectedCollection.elements || [],
            id,
            (node: Element) => ({
                ...node,
                edit: !node.edit,
            }),
        );
        updateElementsForSelectedCollection(updatedElements);
    };

    const handleUpdateElement = (updatedElement: Element) => {
        const updatedElements = replaceNodeById(
            selectedCollection.elements || [],
            updatedElement,
        );
        updateElementsForSelectedCollection(updatedElements);
    };

    const handleSaveElement = (content: string, id: string, dueDate?: string, reminderTime?: string, recurrence?: Task['recurrence'], schedule?: Task['schedule']) => {
        const updatedElements = updateNodeById(
            selectedCollection.elements || [],
            id,
            (node: Element) => {
                if (node.type === 'task') {
                    return {
                        ...node,
                        title: content,
                        edit: false,
                        dueDate,
                        schedule: dueDate ? withScheduleStartDate(schedule, dueDate) : undefined,
                        reminderTime,
                        recurrence,
                    };
                }
                if (node.type === 'note') {
                    return { ...node, content, lastUpdated: new Date().toISOString(), edit: false };
                }
                return { ...node, content, edit: false };
            },
        );
        updateElementsForSelectedCollection(updatedElements);
    };

    const findCollectionById = (id: string | undefined) => {
        for (const folder of folders || []) {
            const collectionInFolder = (folder.collections || []).find((collection) => collection.id === id);
            if (collectionInFolder) return { collection: collectionInFolder, folderId: folder.id };
        }
        return null;
    };

    const handleMoveElementToCollection = (element: Element, targetCollectionId: string | undefined) => {
        const sourceCollectionInfo = findCollectionById(selectedCollectionId);
        const targetCollectionInfo = findCollectionById(targetCollectionId);

        if (!sourceCollectionInfo || !targetCollectionInfo || selectedCollectionId === undefined) return;

        const movedElement = { ...element, groupId: GENERAL_GROUP.id };
        // Remove element from source collection and add to target in a single update
        setFolders(
            folders.map((folder) => {
                // Handle case where source and target are in the same folder
                if (folder.id === sourceCollectionInfo.folderId && folder.id === targetCollectionInfo.folderId) {
                    return {
                        ...folder,
                        collections: (folder.collections || []).map((collection) => {
                            if (collection.id === selectedCollectionId) {
                                return { ...collection, elements: (collection.elements || []).filter((el) => el.id !== element.id) };
                            }
                            if (collection.id === targetCollectionId) {
                                return { ...collection, elements: [...(collection.elements || []), movedElement] };
                            }
                            return collection;
                        }),
                    };
                }
                // Handle case where source and target are in different folders
                if (folder.id === sourceCollectionInfo.folderId) {
                    return {
                        ...folder,
                        collections: (folder.collections || []).map((collection) =>
                            collection.id === selectedCollectionId
                                ? { ...collection, elements: (collection.elements || []).filter((el) => el.id !== element.id) }
                                : collection,
                        ),
                    };
                }
                if (folder.id === targetCollectionInfo.folderId) {
                    return {
                        ...folder,
                        collections: (folder.collections || []).map((collection) =>
                            collection.id === targetCollectionId
                                ? { ...collection, elements: [...(collection.elements || []), movedElement] }
                                : collection,
                        ),
                    };
                }
                return folder;
            }),
        );
    };

    const handleMoveSubtaskToCollection = (subtask: Task, targetCollectionId: string) => {
        const sourceCollectionInfo = findCollectionById(selectedCollectionId);
        const targetCollectionInfo = findCollectionById(targetCollectionId);

        if (!sourceCollectionInfo || !targetCollectionInfo || selectedCollectionId === undefined) return;

        // Function to recursively find and remove a subtask from a task or another subtask
        const findAndRemoveSubtask = (items: Element[]): boolean => {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];

                // Check if this item has the subtask we're looking for (only for task elements)
                if (item.type === 'task' && (item as Task).subtasks && (item as Task).subtasks!.length > 0) {
                    // Check if the subtask is directly in this item's subtasks
                    const subtaskIndex = (item as Task).subtasks!.findIndex(
                        (s) => s.id === subtask.id,
                    );

                    if (subtaskIndex !== -1) {
                        // Found the subtask, remove it from the array
                        (item as Task).subtasks!.splice(subtaskIndex, 1);
                        return true; // Indicate that we found and removed the subtask
                    }

                    // If not found directly, search in nested subtasks
                    if (findAndRemoveSubtask((item as Task).subtasks!)) {
                        return true; // Propagate the success up the recursion chain
                    }
                }
            }
            return false; // Subtask not found in this branch
        };

        // Add subtask as a new task element to target collection and remove from source in a single update
        const newTask: Task = {
            id: generateId(),
            type: 'task',
            title: subtask.title || subtask.task || '',
            completed: subtask.completed,
            subtasks: subtask.subtasks || [],
        };

        setFolders(
            folders.map((folder) => {
                // Handle case where source and target are in the same folder
                if (folder.id === sourceCollectionInfo.folderId && folder.id === targetCollectionInfo.folderId) {
                    return {
                        ...folder,
                        collections: (folder.collections || []).map((collection) => {
                            if (collection.id === selectedCollectionId) {
                                const updatedElements = JSON.parse(JSON.stringify(collection.elements));
                                findAndRemoveSubtask(updatedElements);
                                return { ...collection, elements: updatedElements };
                            }
                            if (collection.id === targetCollectionId) {
                                return { ...collection, elements: [...(collection.elements || []), newTask] };
                            }
                            return collection;
                        }),
                    };
                }
                // Handle case where source and target are in different folders
                if (folder.id === sourceCollectionInfo.folderId) {
                    return {
                        ...folder,
                        collections: (folder.collections || []).map((collection) => {
                            if (collection.id === selectedCollectionId) {
                                const updatedElements = JSON.parse(JSON.stringify(collection.elements));
                                findAndRemoveSubtask(updatedElements);
                                return { ...collection, elements: updatedElements };
                            }
                            return collection;
                        }),
                    };
                }
                if (folder.id === targetCollectionInfo.folderId) {
                    return {
                        ...folder,
                        collections: (folder.collections || []).map((collection) =>
                            collection.id === targetCollectionId
                                ? { ...collection, elements: [...(collection.elements || []), newTask] }
                                : collection,
                        ),
                    };
                }
                return folder;
            }),
        );
    };

    const shareCollection = () => {
        const dataToShare = { ...selectedCollection, shared: true };
        const jsonString = JSON.stringify(dataToShare);
        const utf8Bytes = new TextEncoder().encode(jsonString);
        const encodedData = btoa(String.fromCharCode(...Array.from(utf8Bytes)));
        const shareLink = `${window.location.origin}/share?data=${encodedData}`;

        if (navigator.share) {
            navigator
                .share({
                    title: "MinderBox Collection",
                    text: "Check out this collection on MinderBox!",
                    url: shareLink,
                })
                .catch((error) => console.error("Sharing failed:", error));
        } else {
            // Fallback: Copy the link to clipboard
            navigator.clipboard.writeText(shareLink).then(() => {
                alert("Shareable link copied to clipboard!");
            });
        }
    };

    return (
        <div className="home-screen">
            {selectedCollectionId ? (
                <div className="list-container">
                    <div className="list-container-header">
                        <TaskListHeader
                            listName={selectedCollection.name || ''}
                            onDeleteList={() => onDeleteCollection(selectedCollectionId)}
                            updateListName={updateCollectionName}
                            shareCollection={shareCollection}
                            showReorderHandles={showReorderHandles}
                            onToggleReorderHandles={() => setShowReorderHandles((isVisible) => !isVisible)}
                            onAddSection={() => setShowAddSectionModal(true)}
                            canDeleteCollection={!isInboxCollection}
                            isSecret={Boolean(selectedCollection.secret)}
                            onToggleSecret={isInboxCollection
                                ? undefined
                                : () => onChangeCollectionSecretStatus?.(
                                    selectedCollectionId,
                                    !selectedCollection.secret,
                                )}
                        />
                    </div>
                    <div className="list-container-body">
                        <TaskList
                            todos={selectedCollection.elements || []}
                            setTodos={updateElementsForSelectedCollection}
                            deleteTodo={handleDeleteElement}
                            editTodo={handleEditElement}
                            updateTodo={handleUpdateElement}
                            saveTodo={handleSaveElement}
                            lists={allCollections}
                            selectedListId={selectedCollectionId}
                            moveTaskToList={handleMoveElementToCollection}
                            moveSubtaskToList={handleMoveSubtaskToCollection}
                            moveElementToList={handleMoveElementToCollection}
                            scrollRef={stepsContainerRef}
                            showReorderHandles={showReorderHandles}
                            addElement={handleAddElement}
                            groups={groups}
                            toggleGroup={handleToggleGroup}
                            renameGroup={handleRenameGroup}
                            deleteGroup={handleDeleteGroup}
                        />
                    </div>
                </div>
            ) : (
                <div style={{ padding: "10px" }}>Create a collection to get started!</div>
            )}
            <SectionModal
                isOpen={showAddSectionModal}
                initialName=""
                onClose={() => setShowAddSectionModal(false)}
                onSave={(name) => {
                    handleAddGroup(name);
                    setShowAddSectionModal(false);
                }}
            />
        </div>
    );
};
